import { useCallback, useEffect, useRef, useState } from 'react';

// useAsync — minimal request-state hook. The user opted out of React Query;
// this is the single source of loading/data/error state for one async call.
//
// Usage:
//   const { run, data, loading, error, reset } = useAsync(() => endpoints.users.list());
//   useEffect(() => { run(); }, [run]);
//
// Because callers usually pass an inline arrow function, useAsync stores
// the latest fn in a ref so the returned `run` callback identity stays
// stable across renders (prevents useEffect-loop foot-guns).
export function useAsync(fn, { immediate = false } = {}) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const [state, setState] = useState({
    data: undefined,
    error: undefined,
    loading: Boolean(immediate)
  });

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const run = useCallback(async (...args) => {
    setState({ data: undefined, error: undefined, loading: true });
    try {
      const result = await fnRef.current(...args);
      if (aliveRef.current) {
        setState({ data: result, error: undefined, loading: false });
      }
      return result;
    } catch (err) {
      if (aliveRef.current) {
        setState({ data: undefined, error: err, loading: false });
      }
      throw err;
    }
  }, []);

  const reset = useCallback(() => {
    setState({ data: undefined, error: undefined, loading: false });
  }, []);

  useEffect(() => {
    if (immediate) {
      // Best-effort: fire and forget; errors live on state.
      run().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { ...state, run, reset };
}

// Reactive read of one async source on mount + when `deps` change.
//   const { data, loading, error, refetch } = useQuery(() => endpoints.invoices.list(), [page]);
export function useQuery(fn, deps = []) {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const [state, setState] = useState({ data: undefined, error: undefined, loading: true });
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const refetch = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const result = await fnRef.current();
      if (aliveRef.current) {
        setState({ data: result, error: undefined, loading: false });
      }
      return result;
    } catch (err) {
      if (aliveRef.current) {
        setState({ data: undefined, error: err, loading: false });
      }
      throw err;
    }
  }, []);

  useEffect(() => {
    refetch().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { ...state, refetch };
}
